/**
 * Product Review Widget - Frontend Logic
 * 
 * ฟังก์ชันหลัก:
 * - Lazy Loading: โหลดข้อมูลรีวิวเมื่อผู้ใช้เลื่อนลงมาถึงวิดเจ็ต
 * - ดึงข้อมูลรีวิวผ่าน API ของ Google Apps Script
 * - คำนวณสรุปสถิติและแจกแจงดาว (5 ดาว - 1 ดาว)
 * - แสดงผลรายการรีวิวพร้อมระบบแบ่งหน้าแบบ Local (Load More)
 * - เปิด-ปิดฟอร์มเขียนรีวิวแบบ Drawer/Modal
 * - ตรวจสอบความถูกต้องและส่งฟอร์มโดยหลีกเลี่ยง CORS Block
 */

(function () {
  'use strict';

  console.log("Review Widget: script loaded.");

  // ข้อมูลตั้งค่าหลักและ DOM Elements (จะถูกระบุค่าเมื่อ DOM โหลดเสร็จใน init)
  let widgetContainer = null;
  let config = {
    productId: '',
    productHandle: '',
    productName: '',
    apiUrl: '',
    limit: 5
  };

  // State Management
  let allReviews = [];
  let displayedCount = 0;
  let isExpanded = false; // แสดงรีวิวทั้งหมดและฟิลเตอร์ (false = แสดง 5 รีวิวล่าสุดแบบไม่มีฟิลเตอร์)
  let formOpenTime = 0; // บันทึกเวลาที่เปิดฟอร์มเพื่อกันบอทสแปมความเร็วสูง
  let activeFilterRating = null; // ดาวที่กำลังฟิลเตอร์อยู่ (null = แสดงทั้งหมด)
  let selectedFiles = []; // เก็บอาร์เรย์ของรูปภาพที่ผู้ใช้เลือก: { filename, mimeType, base64 }
  let showOnlyImages = false; // กรองเฉพาะรีวิวที่มีรูปภาพประกอบ (false = แสดงทั้งหมด)
  let searchQuery = ""; // คำสำหรับค้นหารีวิว
  let sortBy = "newest"; // เกณฑ์การเรียงลำดับรีวิว: newest, highest, lowest
  let votedReviews = JSON.parse(localStorage.getItem('r_voted_reviews') || '[]'); // เก็บรหัสรีวิวที่เคยกดโหวตเพื่อป้องกันโหวตซ้ำ

  // DOM Cache
  const elements = {};

  // ==========================================================================
  // 1. Initializer & IntersectionObserver (Lazy Load)
  // ==========================================================================
  function init() {
    console.log("Review Widget: Initializing...");
    widgetContainer = document.getElementById('shopify-product-reviews');
    if (!widgetContainer) {
      console.warn("Review Widget: Container #shopify-product-reviews not found in the DOM. Script stopped.");
      return;
    }

    // กำหนดค่าคอนฟิกจาก Container Attributes
    config.productId = widgetContainer.getAttribute('data-product-id');
    config.productHandle = widgetContainer.getAttribute('data-product-handle');
    config.productName = widgetContainer.getAttribute('data-product-name');
    config.apiUrl = widgetContainer.getAttribute('data-api-url');
    config.hasPurchased = widgetContainer.getAttribute('data-has-purchased') === 'true';
    config.limit = 5;

    // ผูก cache element ทั้งหมดเพื่อประหยัดการ QueryDOM ซ้ำซ้อน
    elements.summarySection = widgetContainer.querySelector('.js-summary-section');
    elements.summaryStats = widgetContainer.querySelector('.js-summary-stats');
    elements.distributionList = widgetContainer.querySelector('.js-distribution');
    elements.reviewList = widgetContainer.querySelector('.js-review-list');
    elements.loadMoreContainer = widgetContainer.querySelector('.js-load-more-container');
    elements.loadMoreBtn = widgetContainer.querySelector('.js-load-more-btn');
    elements.emptyState = widgetContainer.querySelector('.js-empty-state');
    elements.writeReviewBtns = widgetContainer.querySelectorAll('.js-write-review-btn');
    elements.reviewCountBadge = widgetContainer.querySelector('.js-review-count-badge');
    elements.viewAllContainer = widgetContainer.querySelector('.js-view-all-container');
    elements.viewAllBtn = widgetContainer.querySelector('.js-view-all-btn');
    
    // Filter status bar
    elements.filterStatus = widgetContainer.querySelector('.js-filter-status');
    elements.filterStatusText = widgetContainer.querySelector('.js-filter-status-text');
    elements.filterClearBtn = widgetContainer.querySelector('.js-filter-clear-btn');
    elements.filterOptions = widgetContainer.querySelector('.js-filter-options');
    elements.imageFilterPill = widgetContainer.querySelector('.js-image-filter-pill');
    
    // Search & Sort bar selectors
    elements.searchSortBar = widgetContainer.querySelector('.js-search-sort-bar');
    elements.searchInput = widgetContainer.querySelector('.js-search-input');
    elements.sortSelect = widgetContainer.querySelector('.js-sort-select');
    
    // Image Upload & Lightbox
    elements.imageInput = widgetContainer.querySelector('.js-image-input');
    elements.imagePreviewContainer = widgetContainer.querySelector('.js-image-preview-container');
    elements.lightbox = widgetContainer.querySelector('.js-lightbox');
    elements.lightboxImg = widgetContainer.querySelector('.js-lightbox-img');
    elements.lightboxCloseBtn = widgetContainer.querySelector('.js-lightbox-close');
    
    // Modal & Form
    elements.modalOverlay = widgetContainer.querySelector('.js-modal-overlay');
    elements.modalCloseBtns = widgetContainer.querySelectorAll('.js-modal-close');
    elements.reviewForm = widgetContainer.querySelector('.js-review-form');
    elements.formContainer = widgetContainer.querySelector('.js-form-container');
    elements.successState = widgetContainer.querySelector('.js-success-state');
    
    // Inputs & Validation
    elements.starContainer = widgetContainer.querySelector('.js-star-input-container');
    elements.ratingInput = widgetContainer.querySelector('.js-rating-input');
    elements.nameInput = widgetContainer.querySelector('.js-name-input');
    elements.messageInput = widgetContainer.querySelector('.js-message-input');
    elements.honeypotInput = widgetContainer.querySelector('.js-honeypot-input');
    elements.errorMessage = widgetContainer.querySelector('.js-error-message');
    elements.submitBtn = widgetContainer.querySelector('.js-submit-btn');
    elements.submitText = widgetContainer.querySelector('.js-submit-text');
    elements.submitSpinner = widgetContainer.querySelector('.js-submit-spinner');

    console.log("Review Widget: DOM cached successfully.");

    // ติดตั้ง Event Listener ทันทีเพื่อให้ปุ่มกดและฟังก์ชันโต้ตอบทำงานได้ แม้ API URL ยังไม่ได้ระบุ
    setupEventListeners();
    setupStarRatingSelector();

    if (!config.apiUrl) {
      console.warn('Review Widget: Missing Google Apps Script API URL. Visual components will still work, but reviews cannot be loaded/submitted.');
      hideSkeletonLoading();
      showEmptyState();
      return;
    }

    // เริ่มทำงานเมื่อผู้ใช้เลื่อนลงมาถึงเท่านั้นเพื่อ Core Web Vitals
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            fetchReviews();
            obs.unobserve(entry.target);
          }
        });
      }, { rootMargin: '200px' });
      observer.observe(widgetContainer);
    } else {
      // Fallback
      fetchReviews();
    }
  }  // ==========================================================================
  // 2. Event Listeners Setup
  // ==========================================================================
  function setupEventListeners() {
    // โหลดรีวิวเพิ่มเมื่อกดปุ่ม
    if (elements.loadMoreBtn) {
      elements.loadMoreBtn.addEventListener('click', renderMoreReviews);
    }

    // กดเพื่อขยายดูรีวิวทั้งหมด
    if (elements.viewAllBtn) {
      elements.viewAllBtn.addEventListener('click', () => {
        isExpanded = true;
        updateExpansionUI();
        renderInitialReviews();
      });
    }

    // ล้างตัวกรองดาวและรูปภาพ
    if (elements.filterClearBtn) {
      elements.filterClearBtn.addEventListener('click', () => {
        setFilterRating(null);
        setImageFilter(false);
      });
    }

    // กรองเฉพาะรีวิวที่มีรูปภาพประกอบ
    if (elements.imageFilterPill) {
      elements.imageFilterPill.addEventListener('click', () => {
        setImageFilter(!showOnlyImages);
      });
    }

    // จัดการกล่องพิมพ์ค้นหาคำสำคัญ
    if (elements.searchInput) {
      elements.searchInput.addEventListener('input', (e) => {
        searchQuery = e.target.value.trim().toLowerCase();
        renderInitialReviews();
      });
    }

    // จัดการกล่องเลือกเรียงลำดับรีวิว
    if (elements.sortSelect) {
      elements.sortSelect.addEventListener('change', (e) => {
        sortBy = e.target.value;
        renderInitialReviews();
      });
    }

    // ดีลีเกตเหตุการณ์คลิกสำหรับโหวตรีวิวมีประโยชน์ (Helpful Vote)
    if (elements.reviewList) {
      elements.reviewList.addEventListener('click', (e) => {
        const btn = e.target.closest('.js-helpful-btn');
        if (btn) {
          handleHelpfulVote(btn);
        }
      });
    }

    // จัดการเลือกรูปภาพประกอบรีวิว
    if (elements.imageInput) {
      elements.imageInput.addEventListener('change', handleImageSelection);
    }

    // เปิด Modal ฟอร์มเขียนรีวิว
    elements.writeReviewBtns.forEach(btn => {
      btn.addEventListener('click', openReviewForm);
    });

    // ปิด Modal (รองรับทั้งปุ่ม กากบาท และ ปุ่ม ตกลง ในหน้า Success)
    if (elements.modalCloseBtns) {
      elements.modalCloseBtns.forEach(btn => {
        btn.addEventListener('click', closeReviewForm);
      });
    }

    // ปิดเมื่อคลิกนอก Modal (Overlay Background)
    if (elements.modalOverlay) {
      elements.modalOverlay.addEventListener('click', (e) => {
        if (e.target === elements.modalOverlay) {
          closeReviewForm();
        }
      });
    }

    // ตัวปิด Lightbox ขยายรูป
    if (elements.lightboxCloseBtn) {
      elements.lightboxCloseBtn.addEventListener('click', closeLightbox);
    }
    if (elements.lightbox) {
      elements.lightbox.addEventListener('click', (e) => {
        if (e.target === elements.lightbox || e.target.classList.contains('js-lightbox-close')) {
          closeLightbox();
        }
      });
    }

    // จัดการการส่งฟอร์ม (Form Submission)
    if (elements.reviewForm) {
      elements.reviewForm.addEventListener('submit', handleFormSubmit);
    }
  }

  // ==========================================================================
  // 3. API Communication (GET & POST)
  // ==========================================================================
  
  // ดึงข้อมูลรีวิวจาก Google Sheet ผ่าน GET
  async function fetchReviews() {
    try {
      const url = `${config.apiUrl}?product_id=${encodeURIComponent(config.productId)}`;
      const response = await fetch(url);
      
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();

      if (data.success) {
        allReviews = data.reviews || [];
        hideSkeletonLoading();
        
        if (allReviews.length > 0) {
          if (elements.reviewCountBadge) {
            elements.reviewCountBadge.textContent = formatReviewCount(allReviews.length);
          }
          renderSummary(data);
          updateExpansionUI();
          renderInitialReviews();
        } else {
          showEmptyState();
        }
      } else {
        throw new Error(data.error || 'Failed to fetch reviews');
      }
    } catch (err) {
      console.error('Review Widget Fetch Error:', err);
      hideSkeletonLoading();
      showEmptyState();
    }
  }

  // ส่งรีวิวใหม่ผ่าน POST (หลีกเลี่ยง preflight CORS block)
  async function submitReview(payload) {
    if (!config.apiUrl) {
      showSubmitError("ขออภัย ระบบยังไม่ได้ระบุลิงก์ Google Apps Script API URL ในหน้าตั้งค่าธีม Shopify");
      setSubmittingState(false);
      return;
    }
    try {
      // ส่งด้วย Content-Type ที่ไม่ก่อให้เกิด preflight (เช่น text/plain)
      // Google Apps Script doPost จะดึง contents มา parse เป็น JSON แมนนวล
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();

      if (result.success) {
        // สร้างข้อมูลรีวิวจำลองเพื่อแสดงผลบนหน้าจอทันทีแบบ Realtime
        const newReview = {
          review_id: result.review_id || ("RV" + Date.now()),
          customer_name: payload.name,
          rating: payload.rating,
          review: payload.review,
          created_date: result.created_date || "เมื่อสักครู่",
          images: result.images || [],
          admin_reply: result.admin_reply || "",
          helpful_count: 0,
          verified_buyer: payload.verified_buyer
        };
        
        addReviewToLocalStateAndRender(newReview);
        showSuccessState();
      } else {
        throw new Error(result.error || 'Failed to submit review');
      }
    } catch (err) {
      console.error('Review Widget Submit Error:', err);
      showSubmitError(err.message || 'ขออภัย เกิดข้อผิดพลาดในการส่งข้อมูล กรุณาลองใหม่อีกครั้ง');
    } finally {
      setSubmittingState(false);
    }
  }

  // ==========================================================================
  // 4. Rendering Views
  // ==========================================================================

  // ฟอร์แมตจำนวนรีวิวในหัวข้อ (ถ้า < 999 แสดงจำนวนจริง, >= 1000 แสดงแบบ k+)
  function formatReviewCount(count) {
    if (count <= 999) {
      return `(${count})`;
    } else {
      const thousands = Math.floor(count / 1000);
      return `(${thousands}k+)`;
    }
  }

  // อัปเดตการแสดงผลของตัวเลือกการกรองและปุ่มดูรีวิวทั้งหมดตามสถานะการขยาย (isExpanded)
  function updateExpansionUI() {
    if (isExpanded) {
      if (elements.filterOptions) elements.filterOptions.style.display = 'flex';
      if (elements.searchSortBar) elements.searchSortBar.style.display = 'flex';
      if (elements.viewAllContainer) elements.viewAllContainer.style.display = 'none';
    } else {
      if (elements.filterOptions) elements.filterOptions.style.display = 'none';
      if (elements.searchSortBar) elements.searchSortBar.style.display = 'none';
      if (elements.filterStatus) elements.filterStatus.style.display = 'none';
      
      if (elements.viewAllContainer) {
        if (allReviews.length > 5) {
          elements.viewAllContainer.style.display = 'flex';
        } else {
          elements.viewAllContainer.style.display = 'none';
        }
      }
    }
  }

  // ซ่อน Skeleton Loader
  function hideSkeletonLoading() {
    const skeletons = widgetContainer.querySelectorAll('.js-skeleton');
    skeletons.forEach(s => s.style.display = 'none');
  }

  // แสดงผลลัพธ์เปล่า (ยังไม่มีรีวิว)
  function showEmptyState() {
    if (elements.summarySection) elements.summarySection.style.display = 'none';
    if (elements.filterOptions) elements.filterOptions.style.display = 'none';
    if (elements.searchSortBar) elements.searchSortBar.style.display = 'none';
    if (elements.loadMoreContainer) elements.loadMoreContainer.style.display = 'none';
    if (elements.emptyState) elements.emptyState.style.display = 'flex';
  }

  // คำนวณและอัปเดตสถิติสรุปคะแนนรีวิวใหม่ทางฝั่ง Client-side (Realtime)
  function recalculateAndRenderSummary() {
    let totalRating = 0;
    let distribution = { "5": 0, "4": 0, "3": 0, "2": 0, "1": 0 };
    
    allReviews.forEach(r => {
      totalRating += r.rating;
      const key = String(r.rating);
      if (distribution[key] !== undefined) {
        distribution[key]++;
      }
    });
    
    const average = allReviews.length > 0 ? (totalRating / allReviews.length).toFixed(1) : 0;
    
    renderSummary({
      average_rating: parseFloat(average),
      total_count: allReviews.length,
      rating_distribution: distribution
    });
  }

  // แทรกข้อมูลรีวิวใหม่เข้าสู่สถานะระบบและอัปเดตหน้าเพจทันทีโดยไม่ต้องรีโหลด
  function addReviewToLocalStateAndRender(newReview) {
    // ซ่อนหน้าแสดงผลลัพธ์ว่างเปล่าหากเป็นรีวิวแรก
    if (allReviews.length === 0) {
      if (elements.emptyState) elements.emptyState.style.display = "none";
    }
    
    // ใส่รีวิวใหม่ลงในหัวลิสต์
    allReviews.unshift(newReview);
    
    // อัปเดตสถิติและคะแนนดาวสรุป
    recalculateAndRenderSummary();
    
    // อัปเดตตัวเลขจำนวนรีวิวที่หัวข้อ
    if (elements.reviewCountBadge) {
      elements.reviewCountBadge.textContent = formatReviewCount(allReviews.length);
    }

    // อัปเดตการแสดงผลปุ่มดูรีวิวทั้งหมด/ฟิลเตอร์
    updateExpansionUI();
    
    // แสดงรายการรีวิวที่จัดหน้าใหม่
    renderInitialReviews();
  }

  // สร้างและแทรก Schema.org JSON-LD สำหรับช่วยการทำงาน SEO (ดาวเหลืองใน Google Search)
  function injectReviewSchema(avgRating, totalCount) {
    if (totalCount === 0) return;

    // ลบ Schema ตัวเก่าออกก่อนเพื่อป้องกันข้อมูลสะสมซ้ำซ้อน
    const existingSchema = document.getElementById('r-review-jsonld-schema');
    if (existingSchema) {
      existingSchema.remove();
    }

    try {
      const schema = {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": config.productName || document.title,
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": avgRating,
          "reviewCount": totalCount,
          "bestRating": "5",
          "worstRating": "1"
        }
      };

      // พยายามดึงรูปภาพของสินค้าจาก og:image meta tag
      const mainImg = document.querySelector('meta[property="og:image"]');
      if (mainImg && mainImg.content) {
        schema.image = [mainImg.content];
      }

      // พยายามดึง URL canonical ของหน้านั้นๆ
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink && canonicalLink.href) {
        schema.url = canonicalLink.href;
      } else {
        schema.url = window.location.href;
      }

      // ใส่ข้อมูลแบรนด์เพื่อหลีกเลี่ยงการขึ้นคำเตือนใน Google Search Console
      schema.brand = {
        "@type": "Brand",
        "name": "KMA"
      };

      const script = document.createElement('script');
      script.id = 'r-review-jsonld-schema';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      console.log("Review Widget: Dynamic SEO JSON-LD Schema injected/updated.");
    } catch (e) {
      console.warn("Review Widget: Failed to inject SEO schema", e);
    }
  }

  // แสดงผลส่วนสรุปคะแนน (Summary & Distribution Bars)
  function renderSummary(data) {
    if (!elements.summarySection) return;
    elements.summarySection.style.display = 'flex';

    // 1. เรนเดอร์ Average & Stars
    const avg = data.average_rating || 0;
    const total = data.total_count || 0;

    // แทรก Schema สำหรับ SEO แบบเรียลไทม์
    injectReviewSchema(avg, total);
    
    let starsHtml = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= Math.round(avg)) {
        starsHtml += '<span class="r-star-filled">★</span>';
      } else {
        starsHtml += '<span class="r-star-empty">★</span>';
      }
    }

    if (elements.summaryStats) {
      elements.summaryStats.innerHTML = `
        <h3 class="r-summary-avg">${avg.toFixed(1)}</h3>
        <div class="r-summary-stars">${starsHtml}</div>
        <p class="r-summary-count">${total} รีวิว</p>
      `;
    }

    // 2. เรนเดอร์กราฟแท่งความพึงพอใจ 5 - 1 ดาว
    if (elements.distributionList && data.rating_distribution) {
      elements.distributionList.innerHTML = '';
      const dist = data.rating_distribution;

      // วนจาก 5 ไป 1 ดาว
      for (let star = 5; star >= 1; star--) {
        const count = dist[star] || 0;
        // ป้องกันหารด้วยศูนย์
        const pct = total > 0 ? (count / total) * 100 : 0;

        const row = document.createElement('div');
        row.className = 'r-distribution-row';
        row.setAttribute('data-rating', star);
        row.innerHTML = `
          <div class="r-distribution-label">${star} <span class="r-star-filled" style="font-size:12px;">★</span></div>
          <div class="r-distribution-bar-bg">
            <div class="r-distribution-bar-fill" style="width: 0%;"></div>
          </div>
          <div class="r-distribution-count">${count}</div>
        `;

        // สิทธิ์การกดเพื่อกรองรีวิวตามคะแนนดาว
        row.addEventListener('click', () => {
          if (activeFilterRating === star) {
            setFilterRating(null);
          } else {
            setFilterRating(star);
          }
        });

        elements.distributionList.appendChild(row);

        // เพิ่มอนิเมชันให้แท่งค่อยๆ โหลดขึ้นมาทีหลัง
        setTimeout(() => {
          const fill = row.querySelector('.r-distribution-bar-fill');
          if (fill) fill.style.width = `${pct}%`;
        }, 100);
      }

      // แสดงสัญลักษณ์เลือกดาวที่ค้างอยู่ (ถ้ามี)
      if (activeFilterRating !== null) {
        setFilterRating(activeFilterRating);
      }
    }
  }

  // เรนเดอร์รายการรีวิวครั้งแรก
  function renderInitialReviews() {
    if (!elements.reviewList) return;
    elements.reviewList.innerHTML = '';
    displayedCount = 0;

    renderMoreReviews();
  }

  // ดึงรายการรีวิวที่ผ่านการกรองและการจัดเรียง (ดาว, รูปภาพ, คำค้นหา)
  function getFilteredReviews() {
    if (!isExpanded) {
      return allReviews.slice(0, 5);
    }
    let filtered = allReviews;
    
    // 1. กรองตามระดับดาว
    if (activeFilterRating !== null) {
      filtered = filtered.filter(r => Math.round(r.rating) === activeFilterRating);
    }
    
    // 2. กรองเฉพาะรีวิวที่มีรูปภาพประกอบ
    if (showOnlyImages) {
      filtered = filtered.filter(r => r.images && r.images.length > 0);
    }
    
    // 3. กรองตามคำค้นหาข้อความ
    if (searchQuery) {
      filtered = filtered.filter(r => 
        r.customer_name.toLowerCase().includes(searchQuery) ||
        r.review.toLowerCase().includes(searchQuery) ||
        (r.admin_reply && r.admin_reply.toLowerCase().includes(searchQuery))
      );
    }
    
    // 4. จัดเรียงตามเงื่อนไข (Sorting)
    const sorted = [...filtered];
    if (sortBy === "highest") {
      sorted.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === "lowest") {
      sorted.sort((a, b) => a.rating - b.rating);
    }
    
    return sorted;
  }

  // โหลดรีวิวเพิ่มขึ้นมาตาม limit
  function renderMoreReviews() {
    const filteredReviews = getFilteredReviews();
    const end = Math.min(displayedCount + config.limit, filteredReviews.length);
    
    // กรณีไม่มีรีวิวที่ตรงกับเงื่อนไขที่กรองเลย
    if (filteredReviews.length === 0 && (activeFilterRating !== null || showOnlyImages || searchQuery)) {
      let emptyMsg = 'ไม่มีรีวิวที่ตรงกับเงื่อนไขที่เลือก';
      if (searchQuery) {
        emptyMsg = `ไม่พบรีวิวที่ตรงกับคำค้นหา "${escapeHtml(searchQuery)}"`;
      } else if (activeFilterRating !== null && showOnlyImages) {
        emptyMsg = `ไม่มีรีวิว ${activeFilterRating} ดาว ที่มีรูปภาพประกอบ สำหรับสินค้านี้`;
      } else if (activeFilterRating !== null) {
        emptyMsg = `ไม่มีรีวิว ${activeFilterRating} ดาว สำหรับสินค้านี้`;
      } else if (showOnlyImages) {
        emptyMsg = `ไม่มีรีวิวที่มีรูปภาพประกอบ สำหรับสินค้านี้`;
      }
      
      elements.reviewList.innerHTML = `<p style="text-align: center; color: var(--color-text-light); padding: var(--spacing-md) 0; font-size: var(--font-size-sm); width: 100%;">${emptyMsg}</p>`;
      if (elements.loadMoreContainer) {
        elements.loadMoreContainer.style.display = 'none';
      }
      displayedCount = 0;
      return;
    }

    for (let i = displayedCount; i < end; i++) {
      const r = filteredReviews[i];
      const card = document.createElement('div');
      card.className = 'r-card';
      
      let starsHtml = '';
      for (let s = 1; s <= 5; s++) {
        if (s <= r.rating) {
          starsHtml += '<span class="r-star-filled">★</span>';
        } else {
          starsHtml += '<span class="r-star-empty">★</span>';
        }
      }

      // ตรวจสอบภาพประกอบรีวิว
      let imagesHtml = '';
      if (r.images && r.images.length > 0) {
        imagesHtml += '<div class="r-card-images">';
        r.images.forEach(imgUrl => {
          // แปลงลิงก์ Google Drive จาก uc?export=view หรือ open?id เป็น thumbnail เพื่อความปลอดภัยและความเสถียรในการแสดงผลบน Shopify
          let displayUrl = imgUrl;
          if (imgUrl.includes("drive.google.com/uc") || imgUrl.includes("drive.google.com/open") || imgUrl.includes("drive.google.com/file/d")) {
            let fileId = "";
            const idMatch = imgUrl.match(/[?&]id=([^&]+)/);
            if (idMatch && idMatch[1]) {
              fileId = idMatch[1];
            } else {
              const pathMatch = imgUrl.match(/\/file\/d\/([^/]+)/);
              if (pathMatch && pathMatch[1]) {
                fileId = pathMatch[1];
              }
            }
            if (fileId) {
              displayUrl = "https://drive.google.com/thumbnail?id=" + fileId + "&sz=w800";
            }
          }
          imagesHtml += `<img class="r-card-img js-card-img" src="${displayUrl}" alt="ภาพรีวิวจากลูกค้า" data-zoom="${displayUrl}">`;
        });
        imagesHtml += '</div>';
      }

      // ตรวจสอบคำตอบกลับจากร้านค้า (Admin Reply)
      let replyHtml = '';
      if (r.admin_reply) {
        replyHtml = `
          <div class="r-admin-reply">
            <div class="r-admin-reply-header">
              <span class="r-admin-badge">แอดมินร้านค้า</span>
              <span class="r-admin-reply-title">ตอบกลับจากแอดมิน</span>
            </div>
            <p class="r-admin-reply-content">${escapeHtml(r.admin_reply)}</p>
          </div>
        `;
      }

      // ตรวจสอบความถูกต้องของผู้ซื้อจริง (Verified Buyer) เพื่อแสดงป้ายกำกับ
      let verifiedHtml = '';
      if (r.verified_buyer) {
        verifiedHtml = `<span class="r-verified-badge"><span class="r-verified-check">✓</span> ผู้ซื้อจริง</span>`;
      }

      // ตรวจสอบสถานะการโหวตมีประโยชน์ (Helpful Vote) เพื่อใส่สีปุ่มและล็อกสถานะ
      const isVoted = votedReviews.includes(r.review_id);
      const votedClass = isVoted ? 'r-voted' : '';
      const votedDisabled = isVoted ? 'disabled' : '';

      card.innerHTML = `
        <div class="r-card-header">
          <div class="r-card-author-info">
            <div class="r-card-author-row">
              <span class="r-card-author">${escapeHtml(r.customer_name)}</span>
              ${verifiedHtml}
            </div>
            <div class="r-card-stars">${starsHtml}</div>
          </div>
          <span class="r-card-date">${r.created_date}</span>
        </div>
        <p class="r-card-content">${escapeHtml(r.review)}</p>
        ${imagesHtml}
        ${replyHtml}
        <div class="r-card-footer">
          <button type="button" class="r-helpful-btn js-helpful-btn ${votedClass}" ${votedDisabled} data-review-id="${r.review_id}">
            <span class="r-thumb-icon">👍</span> มีประโยชน์ (<span class="js-helpful-count">${r.helpful_count || 0}</span>)
          </button>
        </div>
      `;

      // ผูกอีเวนต์คลิกซูมขยายรูปภาพ (Lightbox)
      const cardImgs = card.querySelectorAll('.js-card-img');
      cardImgs.forEach(img => {
        img.addEventListener('click', () => {
          openLightbox(img.getAttribute('data-zoom'));
        });
      });

      elements.reviewList.appendChild(card);
    }

    displayedCount = end;

    // ซ่อน/แสดงปุ่มโหลดเพิ่ม
    if (elements.loadMoreContainer) {
      if (!isExpanded || displayedCount >= filteredReviews.length) {
        elements.loadMoreContainer.style.display = 'none';
      } else {
        elements.loadMoreContainer.style.display = 'flex';
      }
    }
  }

  // ตั้งค่าและควบคุมการกรองคะแนนดาว
  function setFilterRating(rating) {
    activeFilterRating = rating;

    // 1. ไฮไลต์แถวกราฟที่ถูกเลือก
    if (elements.distributionList) {
      const rows = elements.distributionList.querySelectorAll('.r-distribution-row');
      rows.forEach(r => {
        const rowRating = parseInt(r.getAttribute('data-rating'), 10);
        if (rowRating === rating) {
          r.classList.add('r-selected');
        } else {
          r.classList.remove('r-selected');
        }
      });
    }

    // 2. ปรับปรุงสเตตัสข้อความใน UI
    updateFilterStatusBar();

    // 3. เริ่มเรนเดอร์รีวิวใหม่
    renderInitialReviews();
  }

  // ตั้งค่าและควบคุมตัวกรองแสดงเฉพาะที่มีรูปภาพ
  function setImageFilter(active) {
    showOnlyImages = active;
    
    if (elements.imageFilterPill) {
      if (active) {
        elements.imageFilterPill.classList.add('active');
      } else {
        elements.imageFilterPill.classList.remove('active');
      }
    }

    // ปรับปรุงสเตตัสข้อความใน UI
    updateFilterStatusBar();

    // เริ่มเรนเดอร์รีวิวใหม่
    renderInitialReviews();
  }

  // ปรับปรุงการแสดงข้อความสเตตัสการกรองดาวและรูปภาพประกอบ
  function updateFilterStatusBar() {
    if (!elements.filterStatus || !elements.filterStatusText) return;

    const hasStars = (activeFilterRating !== null);
    const hasImages = showOnlyImages;

    if (hasStars || hasImages) {
      let text = "กำลังแสดง";
      if (hasStars && hasImages) {
        text += `เฉพาะรีวิว ${activeFilterRating} ดาว ที่มีรูปภาพประกอบ`;
      } else if (hasStars) {
        text += `เฉพาะรีวิว ${activeFilterRating} ดาว`;
      } else if (hasImages) {
        text += "เฉพาะรีวิวที่มีรูปภาพประกอบ";
      }
      elements.filterStatusText.textContent = text;
      elements.filterStatus.style.display = 'flex';
    } else {
      elements.filterStatus.style.display = 'none';
      elements.filterStatusText.textContent = '';
    }
  }

  // จัดการการส่งโหวตมีประโยชน์ (Helpful Vote) ไปยัง Google Sheets
  async function handleHelpfulVote(btn) {
    const reviewId = btn.getAttribute('data-review-id');
    if (!reviewId || votedReviews.includes(reviewId)) return;

    // ล็อกปุ่มเพื่อป้องกันการกดเบิ้ลรัว
    btn.disabled = true;

    try {
      const payload = {
        action: "vote_helpful",
        review_id: reviewId
      };

      const response = await fetch(config.apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();

      if (result.success) {
        // 1. บันทึก ID ลงใน Voted ใน LocalStorage
        votedReviews.push(reviewId);
        localStorage.setItem('r_voted_reviews', JSON.stringify(votedReviews));

        // 2. อัปเดต helpful_count ในหน่วยความจำเพื่อให้คงค่าเวลาเกิดการ Sort/Filter
        const targetReview = allReviews.find(r => r.review_id === reviewId);
        if (targetReview) {
          targetReview.helpful_count = result.helpful_count || (targetReview.helpful_count + 1);
        }

        // 3. ปรับสไตล์ปุ่มและการแสดงผลใน DOM
        btn.classList.add('r-voted');
        const countSpan = btn.querySelector('.js-helpful-count');
        if (countSpan) {
          countSpan.textContent = result.helpful_count || (parseInt(countSpan.textContent, 10) + 1);
        }
      } else {
        throw new Error(result.error || 'Failed to register vote');
      }
    } catch (err) {
      console.error('Helpful Vote Error:', err);
      // ปลดล็อกปุ่มหากส่งไม่สำเร็จเพื่อให้ลองใหม่ได้
      btn.disabled = false;
    }
  }

  // Helper สำหรับ Escape HTML ป้องกัน XSS
  function escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ==========================================================================
  // 5. Star Rating Selector (ตัวฟอร์มเลือกคะแนนดาว)
  // ==========================================================================
  function setupStarRatingSelector() {
    if (!elements.starContainer) return;
    
    const stars = elements.starContainer.querySelectorAll('.js-star-btn');
    
    stars.forEach(star => {
      star.addEventListener('click', () => {
        const rating = star.getAttribute('data-value');
        
        elements.starContainer.setAttribute('data-rating', rating);
        elements.starContainer.classList.add('r-has-rating');
        if (elements.ratingInput) elements.ratingInput.value = rating;

        // ไฮไลต์ดาวที่ถูกเลือกและก่อนหน้า
        stars.forEach(s => {
          const val = parseInt(s.getAttribute('data-value'), 10);
          if (val === parseInt(rating, 10)) {
            s.classList.add('active');
          } else {
            s.classList.remove('active');
          }
        });

        // ล้างข้อความแจ้งเตือนข้อผิดพลาดเมื่อเริ่มกรอกคะแนน
        hideSubmitError();
      });
    });
  }

  // ==========================================================================
  // 6. Modal / Form Actions & Validation
  // ==========================================================================
  function openReviewForm() {
    if (!elements.modalOverlay) return;
    elements.modalOverlay.classList.add('r-open');
    document.body.style.overflow = 'hidden'; // ล็อกการเลื่อนหน้าจอหลัก
    formOpenTime = Date.now(); // เก็บเวลาที่ผู้ใช้เริ่มเปิดฟอร์มเพื่อกันบอทสแปม

    // รีเซ็ตสถานะภายในฟอร์ม
    if (elements.formContainer) elements.formContainer.style.display = 'block';
    if (elements.successState) elements.successState.style.display = 'none';
    resetForm();
  }

  function closeReviewForm() {
    if (!elements.modalOverlay) return;
    elements.modalOverlay.classList.remove('r-open');
    document.body.style.overflow = ''; // คืนค่าการเลื่อนหน้าจอหลัก
  }

  function resetForm() {
    if (elements.reviewForm) elements.reviewForm.reset();
    if (elements.starContainer) {
      elements.starContainer.removeAttribute('data-rating');
      elements.starContainer.classList.remove('r-has-rating');
      elements.starContainer.querySelectorAll('.js-star-btn').forEach(s => s.classList.remove('active'));
    }
    if (elements.ratingInput) elements.ratingInput.value = '';
    
    // ล้างค่ารูปภาพประกอบรีวิว
    selectedFiles = [];
    if (elements.imagePreviewContainer) elements.imagePreviewContainer.innerHTML = '';
    if (elements.imageInput) if (elements.imageInput) elements.imageInput.value = '';

    hideSubmitError();
  }

  // การส่งข้อมูลและ Validation
  function handleFormSubmit(e) {
    e.preventDefault();
    hideSubmitError();

    // 1. Validation
    const name = elements.nameInput ? elements.nameInput.value.trim() : "";
    const rating = elements.ratingInput ? parseInt(elements.ratingInput.value, 10) : 0;
    const review = elements.messageInput ? elements.messageInput.value.trim() : "";
    const honeypot = elements.honeypotInput ? elements.honeypotInput.value : "";

    if (!name) {
      showSubmitError('กรุณากรอกชื่อของคุณ');
      return;
    }
    if (!rating || rating < 1 || rating > 5) {
      showSubmitError('กรุณาเลือกคะแนนประเมิน (1-5 ดาว)');
      return;
    }

    // 2. Honeypot check (ดักจับบอท)
    if (honeypot !== '') {
      // ถ้าฟิลด์ลับถูกกรอก ให้จำลองสถานะส่งสำเร็จเพื่อหลอกบอท โดยไม่ต้องยิง API จริง
      showSuccessState();
      return;
    }

    // 3. Time-based spam check (ดักจับสคริปต์ส่งอัตโนมัติแบบเสี้ยววินาที)
    const elapsedSeconds = (Date.now() - formOpenTime) / 1000;
    if (elapsedSeconds < 2) {
      // หากส่งเร็วเกิน 2 วินาที อาจเป็นการกรอกของโปรแกรมบอท
      showSuccessState();
      return;
    }

    // 4. เริ่มส่งข้อมูล
    setSubmittingState(true);

    const payload = {
      product_id: config.productId,
      product_handle: config.productHandle,
      product_name: config.productName,
      name: name,
      rating: rating,
      review: review,
      images: selectedFiles, // แนบรูปภาพ Base64
      verified_buyer: config.hasPurchased
    };

    submitReview(payload);
  }

  // ปรับเปลี่ยนสถานะปุ่มส่ง (ขณะกำลังรอ API Response)
  function setSubmittingState(isSubmitting) {
    if (!elements.submitBtn) return;
    
    if (isSubmitting) {
      elements.submitBtn.disabled = true;
      if (elements.submitSpinner) elements.submitSpinner.style.display = 'inline-block';
      if (elements.submitText) elements.submitText.textContent = 'กำลังส่งรีวิว...';
    } else {
      elements.submitBtn.disabled = false;
      if (elements.submitSpinner) elements.submitSpinner.style.display = 'none';
      if (elements.submitText) elements.submitText.textContent = 'ส่งรีวิว';
    }
  }

  // จัดการข้อความ error
  function showSubmitError(msg) {
    if (!elements.errorMessage) return;
    elements.errorMessage.textContent = msg;
    elements.errorMessage.style.display = 'block';
  }

  function hideSubmitError() {
    if (elements.errorMessage) {
      elements.errorMessage.style.display = 'none';
      elements.errorMessage.textContent = '';
    }
  }

  // แสดงหน้ารายงานผลลัพธ์เมื่อเพิ่มข้อมูลสำเร็จ
  function showSuccessState() {
    if (elements.formContainer) elements.formContainer.style.display = 'none';
    if (elements.successState) elements.successState.style.display = 'flex';
  }

  // ==========================================================================
  // 7. Image Upload & Lightbox Functions
  // ==========================================================================
  function handleImageSelection(e) {
    hideSubmitError();
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // ตรวจสอบจำนวนรูปภาพรวมสูงสุด 2 รูป
    if (selectedFiles.length + files.length > 2) {
      showSubmitError('คุณสามารถแนบรูปภาพได้สูงสุด 2 รูปเท่านั้น');
      if (elements.imageInput) elements.imageInput.value = '';
      return;
    }

    // ตรวจสอบขนาดสูงสุดรูปละ 5MB
    const maxSize = 5 * 1024 * 1024;
    for (let file of files) {
      if (file.size > maxSize) {
        showSubmitError(`ไฟล์รูปภาพ "${file.name}" มีขนาดใหญ่เกิน 5MB`);
        if (elements.imageInput) elements.imageInput.value = '';
        return;
      }
    }

    // แปลงรูปเป็น Base64 และสร้างพรีวิว
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = function(event) {
        const base64Content = event.target.result;
        const fileObj = {
          filename: file.name,
          mimeType: file.type,
          base64: base64Content
        };
        selectedFiles.push(fileObj);

        // วาดตัวอย่างภาพ
        renderImagePreview(fileObj);
      };
      reader.readAsDataURL(file);
    });

    // ล้างค่า input เพื่อให้สามารถเลือกไฟล์เดิมซ้ำได้อีกรอบ
    if (elements.imageInput) elements.imageInput.value = '';
  }

  // วาดพรีวิวรูปภาพในหน้าฟอร์มพร้อมปุ่มกากบาทลบรูป
  function renderImagePreview(fileObj) {
    if (!elements.imagePreviewContainer) return;

    const thumb = document.createElement('div');
    thumb.className = 'r-preview-thumb';
    thumb.innerHTML = `
      <img src="${fileObj.base64}" alt="${escapeHtml(fileObj.filename)}">
      <button class="r-preview-remove" type="button" aria-label="Remove image">&times;</button>
    `;

    // จัดการลบพรีวิวรูปภาพออก
    thumb.querySelector('.r-preview-remove').addEventListener('click', () => {
      selectedFiles = selectedFiles.filter(item => item !== fileObj);
      thumb.remove();
    });

    elements.imagePreviewContainer.appendChild(thumb);
  }

  // เปิด-ปิด Lightbox ซูมรูปภาพรีวิว
  function openLightbox(url) {
    if (!elements.lightbox || !elements.lightboxImg) return;
    let finalUrl = url;
    if (url.includes("drive.google.com/thumbnail")) {
      finalUrl = url.replace("sz=w800", "sz=w1600");
    }
    elements.lightboxImg.src = finalUrl;
    elements.lightbox.style.display = 'flex';
    document.body.style.overflow = 'hidden'; // ล็อกการเลื่อนหน้าเว็บหลัก
  }

  function closeLightbox() {
    if (!elements.lightbox || !elements.lightboxImg) return;
    elements.lightbox.style.display = 'none';
    elements.lightboxImg.src = '';
    
    // ปลดล็อกเลื่อนจอถ้ารายการ Modal ฟอร์มหลักถูกปิดอยู่เช่นกัน
    if (!elements.modalOverlay.classList.contains('r-open')) {
      document.body.style.overflow = '';
    }
  }

  // เรียกใช้ตัวเริ่มต้นระบบ
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
